/** @typedef {import('headlight-configurable-application').Pict} Pict */

/** @type {typeof import('pict-section-form').PictInputExtensionProvider} */
const PictSectionInputExtension_TabSectionSelector = require('pict-section-form/source/providers/inputs/Pict-Provider-Input-TabSectionSelector.js');

const libTabPrintHostCSS = require('./_TabPrintHostCSS.js');

class TabSectionSelectorInputHandler extends PictSectionInputExtension_TabSectionSelector
{
	/**
	 * @param {Object} pFable - The Fable instance.
	 * @param {Object} [pOptions] - The provider options.
	 * @param {String} [pServiceHash] - The fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		// Shared with TabGroupSelector -- see _TabPrintHostCSS.js for why this registers here and
		// injects at init rather than doing both in the constructor.
		libTabPrintHostCSS.registerPrintHostCSS(this.pict);
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLTabSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @returns {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLTabSelector, pTransactionGUID)
	{
		// Before the print early-return on purpose: the print-host rule is exactly what the PDF needs.
		libTabPrintHostCSS.injectPrintHostCSS(this.pict);

		if (!this.pict.AppData.Interactive && pInput.PictForm?.Exclusive !== true)
		{
			// show all tabs in print mode unless Exclusive is set to true
			return;
		}
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLTabSelector, pTransactionGUID);
	}

	/**
	 * Tier-3 (Half A) a11y: after the base provider swaps the visible tab/panel
	 * via CSS classes, mirror the same state into ARIA so assistive tech
	 * announces the tabs as a real tablist. We deliberately do NOT add roving
	 * tabindex or arrow-key navigation (Half B) — every tab stays natively
	 * Tab-focusable, which is already WCAG 2.x keyboard-operable.
	 *
	 * `role="tablist"`/`role="tab"`/`role="presentation"` are emitted statically
	 * by templates/InputType-TabSectionSelector.js; only the stateful bits
	 * (aria-selected) and the cross-element relationships (tab <-> panel, whose
	 * panel id carries the runtime metacontroller UUID) are set here. Idempotent:
	 * runs once on init and again on every tab switch.
	 *
	 * @param {string} pViewHash
	 * @param {string} pInputHash
	 * @param {string} pTabViewHash
	 *
	 * @return {boolean}
	 */
	selectTabByViewHash(pViewHash, pInputHash, pTabViewHash)
	{
		const tmpResult = super.selectTabByViewHash(pViewHash, pInputHash, pTabViewHash);
		if (tmpResult === false)
		{
			return tmpResult;
		}

		const tmpView = this.pict.views[pViewHash];
		const tmpInput = tmpView ? tmpView.getInputFromHash(pInputHash) : null;
		if (!tmpInput || !Array.isArray(tmpInput?.PictForm?.TabSectionSet))
		{
			return tmpResult;
		}

		for (let i = 0; i < tmpInput.PictForm.TabSectionSet.length; i++)
		{
			const tmpTabSectionHash = tmpInput.PictForm.TabSectionSet[i];
			const tmpTabID = `TAB-${tmpTabSectionHash}-${tmpInput.Macro.RawHTMLID}`;
			const tmpTabSelector = `#${tmpTabID} a`;
			const tmpPanelSelector = this.getSectionSelector(tmpTabSectionHash);
			const tmpIsActive = (tmpTabSectionHash === pTabViewHash);

			// State: the active tab is the selected one.
			this.pict.ContentAssignment.setAttribute(tmpTabSelector, 'aria-selected', tmpIsActive ? 'true' : 'false');
			// Relationship: tab controls its panel; panel is a tabpanel labelled by its tab.
			this.pict.ContentAssignment.setAttribute(tmpTabSelector, 'aria-controls', tmpPanelSelector.replace(/^#/, ''));
			this.pict.ContentAssignment.setAttribute(tmpPanelSelector, 'role', 'tabpanel');
			this.pict.ContentAssignment.setAttribute(tmpPanelSelector, 'aria-labelledby', tmpTabID);
		}

		return tmpResult;
	}
}

module.exports = TabSectionSelectorInputHandler;
